import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useMemo, useRef, useState } from 'react';
import {
  GridDetailField,
  GridDetailFieldsGrid,
  GridDetailFieldsGridFull,
  GridDetailPopup,
  GridDetailSection,
  OPS_SITE_GRID_PROPS,
  OpsGridStateStoring,
  OpsGridToolbarItems,
  createRemoteGridFilterOptionChangedHandler,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  opsGridDetailPopupSizeKey
} from '../opsDataGrid';
import { formatAuditDateTime, formatMountLabel, formatOperationLabel } from './formatters';
import type { StorageFileAuditEntry, StorageFileAuditLoadRequest } from './types';

export interface StorageFileAuditGridProps {
  loadPage: (request: StorageFileAuditLoadRequest) => Promise<{ total: number; items: StorageFileAuditEntry[] }>;
  pageSize?: number;
  stateStorageKey: string;
}

export const STORAGE_FILE_AUDIT_GRID_SCHEMA_VERSION = 1;

export const STORAGE_FILE_AUDIT_GRID_COLUMNS = [
  'occurredAt',
  'operation',
  'success',
  'userName',
  'roles',
  'tenantId',
  'mountId',
  'path',
  'errorText'
] as const;

type SortDescriptor = {
  selector?: unknown;
  desc?: boolean;
};

function getFirstSort(sort: unknown): SortDescriptor | null {
  if (!sort) return null;
  if (Array.isArray(sort)) return (sort[0] as SortDescriptor | undefined) ?? null;
  return sort as SortDescriptor;
}

function buildLoadRequest(
  options: {
    skip?: number;
    take?: number;
    searchValue?: unknown;
    filter?: unknown;
    sort?: unknown;
  },
  fallbackTake: number
): StorageFileAuditLoadRequest {
  const sort = getFirstSort(options.sort);
  const selector = typeof sort?.selector === 'string' ? sort.selector : undefined;
  const search = typeof options.searchValue === 'string' ? options.searchValue.trim() : '';
  return {
    skip: typeof options.skip === 'number' ? options.skip : 0,
    take: typeof options.take === 'number' ? options.take : fallbackTake,
    search: search || undefined,
    filter: options.filter ? JSON.stringify(options.filter) : undefined,
    sortBy: selector,
    sortDesc: sort?.desc
  };
}

function ResultBadge({ success, errorText }: { success: boolean; errorText: string | null }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.125rem 0.5rem',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: success ? 'var(--ops-status-succeeded-bg, #dcfce7)' : 'var(--ops-status-failed-bg, #fee2e2)',
        color: success ? 'var(--ops-status-succeeded-fg, #166534)' : 'var(--ops-status-failed-fg, #991b1b)'
      }}
      title={errorText ?? undefined}
    >
      {success ? 'OK' : (errorText ?? 'Failed')}
    </span>
  );
}

function StorageFileAuditDetailView({ entry }: { entry: StorageFileAuditEntry }) {
  return (
    <>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ops-color-text, #111827)' }}>
          {formatOperationLabel(entry.operation)}
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ops-color-muted, #64748b)', fontFamily: 'monospace' }}>
          {entry.id}
        </div>
      </div>
      <GridDetailSection title="Event">
        <GridDetailFieldsGrid>
          <GridDetailField label="When">{formatAuditDateTime(entry.occurredAt)}</GridDetailField>
          <GridDetailField label="Result">
            <ResultBadge success={entry.success} errorText={entry.errorText} />
          </GridDetailField>
          <GridDetailFieldsGridFull>
            <GridDetailField label="Path" muted={!entry.path}>
              {entry.path || '—'}
            </GridDetailField>
          </GridDetailFieldsGridFull>
          <GridDetailField label="Mount" muted={!entry.mountId}>
            {formatMountLabel(entry.mountId)}
          </GridDetailField>
          {entry.errorText ? (
            <GridDetailFieldsGridFull>
              <GridDetailField label="Error">{entry.errorText}</GridDetailField>
            </GridDetailFieldsGridFull>
          ) : null}
        </GridDetailFieldsGrid>
      </GridDetailSection>
      <GridDetailSection title="Actor">
        <GridDetailFieldsGrid>
          <GridDetailField label="User" muted={!entry.userName}>
            {entry.userName ?? '—'}
          </GridDetailField>
          <GridDetailField label="Roles" muted={!entry.roles}>
            {entry.roles ?? '—'}
          </GridDetailField>
          <GridDetailFieldsGridFull>
            <GridDetailField label="Tenant" muted={!entry.tenantId}>
              {entry.tenantId ?? '—'}
            </GridDetailField>
          </GridDetailFieldsGridFull>
        </GridDetailFieldsGrid>
      </GridDetailSection>
    </>
  );
}

export function StorageFileAuditGrid({
  loadPage,
  pageSize = 100,
  stateStorageKey
}: StorageFileAuditGridProps) {
  const gridRef = useRef<DataGridRef>(null);
  const [viewEntry, setViewEntry] = useState<StorageFileAuditEntry | null>(null);

  const dataSource = useMemo(() => {
    const store = new CustomStore<StorageFileAuditEntry, string>({
      key: 'id',
      load: async (options) => {
        const page = await loadPage(buildLoadRequest(options, pageSize));
        return { data: page.items, totalCount: page.total };
      }
    });
    return new DataSource({ store, pageSize, paginate: true });
  }, [loadPage, pageSize]);

  const onRemoteFilterOptionChanged = useMemo(
    () => createRemoteGridFilterOptionChangedHandler(dataSource),
    [dataSource]
  );

  return (
    <>
      <DataGrid
        ref={gridRef}
        dataSource={dataSource}
        keyExpr="id"
        height="100%"
        width="100%"
        showBorders
        columnAutoWidth
        columnHidingEnabled
        allowColumnResizing
        allowColumnReordering
        columnResizingMode="widget"
        {...OPS_SITE_GRID_PROPS}
        wordWrapEnabled
        remoteOperations={{ paging: true, filtering: true, sorting: true }}
        onOptionChanged={onRemoteFilterOptionChanged}
        onRowDblClick={(e) => onGridRowDblClick(e, setViewEntry)}
        onKeyDown={(e) => onGridFocusedRowEnter(e, setViewEntry)}
      >
        <OpsGridStateStoring
          storageKey={stateStorageKey}
          schemaVersion={STORAGE_FILE_AUDIT_GRID_SCHEMA_VERSION}
          columns={STORAGE_FILE_AUDIT_GRID_COLUMNS}
        />
        <Toolbar>
          <OpsGridToolbarItems
            gridRef={gridRef}
            onRefresh={() => void dataSource.reload()}
            refreshHint="Reload audit events"
            export={{
              fileName: 'file-operations-export',
              worksheetName: 'File operations',
              hint: 'Export audit events'
            }}
            resetLayout={{ storageKey: stateStorageKey }}
          />
          <Item name="searchPanel" location="after" />
        </Toolbar>
        <SearchPanel visible width={220} placeholder="Search…" />
        <HeaderFilter visible />
        <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
        <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
        <Paging defaultPageSize={pageSize} />
        <Pager
          visible
          showInfo
          showNavigationButtons
          showPageSizeSelector
          allowedPageSizes={[100, 250, 500, 1000]}
          displayMode="full"
        />
        <Column
          name="occurredAt"
          dataField="occurredAt"
          caption="When"
          dataType="datetime"
          sortOrder="desc"
          width={180}
          calculateDisplayValue={(row: StorageFileAuditEntry) => formatAuditDateTime(row.occurredAt)}
        />
        <Column
          name="operation"
          dataField="operation"
          caption="Operation"
          dataType="string"
          width={130}
          allowHeaderFiltering={false}
          calculateDisplayValue={(row: StorageFileAuditEntry) => formatOperationLabel(row.operation)}
        />
        <Column
          name="success"
          dataField="success"
          caption="Result"
          dataType="boolean"
          width={120}
          trueText="OK"
          falseText="Failed"
          cellRender={(c) => (
            <ResultBadge success={Boolean(c.value)} errorText={(c.data as StorageFileAuditEntry).errorText} />
          )}
        />
        <Column name="userName" dataField="userName" caption="User" minWidth={160} />
        <Column name="roles" dataField="roles" caption="Roles" minWidth={140} />
        <Column name="tenantId" dataField="tenantId" caption="Tenant" minWidth={220} />
        <Column
          name="mountId"
          dataField="mountId"
          caption="Mount"
          minWidth={220}
          allowHeaderFiltering={false}
          calculateDisplayValue={(row: StorageFileAuditEntry) => formatMountLabel(row.mountId)}
        />
        <Column name="path" dataField="path" caption="Path" minWidth={280} />
        <Column name="errorText" dataField="errorText" caption="Error" minWidth={220} />
      </DataGrid>

      <GridDetailPopup
        visible={viewEntry !== null}
        title="File operation details"
        onClose={() => setViewEntry(null)}
        width={560}
        sizeStorageKey={opsGridDetailPopupSizeKey(stateStorageKey)}
      >
        {viewEntry ? <StorageFileAuditDetailView entry={viewEntry} /> : null}
      </GridDetailPopup>
    </>
  );
}
